import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { PawPrintIcon as PawPrint, PlusIcon as Plus, CalendarBlankIcon as CalendarBlank, TrashIcon as Trash } from "@phosphor-icons/react";
import WatchButton from "@/components/WatchButton";
import { formatDate, formatApiError } from "@/lib/utils-app";
import EmptyState from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/SkeletonList";
import { isNativeApp } from "@/lib/nativeBridge";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const DOLLARS = (cents) => (cents == null ? "—" : `$${(cents / 100).toFixed(0)}`);

function DeleteLitterButton({ endpoint, title, description, testId, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(endpoint);
      toast.success("Deleted");
      onDeleted();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          className="relative z-10 h-8 w-8 inline-flex items-center justify-center rounded-full border border-[#EAEAEA] text-[#9B9B9B] hover:text-crimson hover:border-crimson/40 transition-colors flex-shrink-0"
          data-testid={testId}
          title="Delete"
        >
          <Trash size={14} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={doDelete}
            disabled={busy}
            className="rounded-full bg-crimson hover:bg-crimson/90 text-white"
          >
            {busy ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Litters() {
  const [tab, setTab] = useState("registered");
  const [litters, setLitters] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/litters").then((r) => setLitters(r.data || [])).catch(() => {}),
      api
        .get("/upcoming-breedings/me")
        .then((r) => setUpcoming(r.data || []))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
            Litters
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            My litters
          </h1>
          <p className="text-[13px] text-[#6B6B6B] mt-2 max-w-xl">
            Track every litter from planned breeding to whelp to placement.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/litters/upcoming/new">
            <Button
              variant="outline"
              className="rounded-full px-5"
              data-testid="add-upcoming-litter-btn"
            >
              <CalendarBlank size={16} className="mr-1" /> Plan upcoming
            </Button>
          </Link>
          <Link to="/litters/new">
            <Button
              className="bg-ink hover:bg-crimson text-white rounded-full px-6"
              data-testid="add-litter-btn"
            >
              <Plus size={16} className="mr-1" /> New litter
            </Button>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#EAEAEA] mb-6">
        {[
          ["registered", `Registered (${litters.length})`],
          ["upcoming", `Upcoming (${upcoming.length})`],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`litters-tab-${k}`}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold border-b-2 transition-colors ${
              tab === k
                ? "border-crimson text-ink"
                : "border-transparent text-[#9B9B9B] hover:text-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && (
        <CardGridSkeleton count={4} withImage={false} testId="litters-loading-skeleton" />
      )}

      {!loading && tab === "registered" && (
        litters.length === 0 ? (
          <EmptyState
            testId="litters-empty"
            icon={<PawPrint size={32} weight="duotone" />}
            title="No litters yet"
            description={isNativeApp()
              ? "Record a litter and we'll auto-register every puppy in one motion."
              : "Record a litter and we'll auto-register every puppy in one motion. Pro and Kennel Elite plans cover unlimited litters."}
            ctaLabel="Record a litter"
            ctaTo="/litters/new"
            secondaryLabel={isNativeApp() ? undefined : "See plans"}
            secondaryTo={isNativeApp() ? undefined : "/billing"}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {litters.map((l) => (
              <div
                key={l.id}
                className="bg-white border border-[#EAEAEA] rounded-xl p-6 shadow-luxury-light"
                data-testid={`litter-card-${l.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-spectral text-2xl tracking-tight">
                      {l.sire_name} × {l.dam_name}
                    </h3>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mt-1">
                      Whelped {formatDate(l.whelp_date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-[#C1272D]/10 text-crimson border border-[#C1272D]/20 rounded-full px-3 py-1 text-xs font-semibold">
                      {l.puppy_ids?.length || 0} pups
                    </span>
                    <WatchButton targetType="litter" targetId={l.id} />
                    <DeleteLitterButton
                      endpoint={`/litters/${l.id}`}
                      title={`Delete litter ${l.sire_name} × ${l.dam_name}?`}
                      description="This removes the litter record. Puppies already registered from this litter stay registered — only the litter entry is deleted. This can't be undone."
                      testId={`delete-litter-${l.id}`}
                      onDeleted={() => setLitters((prev) => prev.filter((x) => x.id !== l.id))}
                    />
                  </div>
                </div>
                {l.notes && <p className="text-sm text-[#6B6B6B] mb-3">{l.notes}</p>}
                <div className="flex flex-wrap gap-2">
                  {(l.puppy_ids || []).map((pid) => (
                    <Link
                      key={pid}
                      to={`/dogs/${pid}`}
                      className="text-xs bg-[#FAFAFA] border border-[#E5E5E5] rounded-full px-3 py-1 text-ink hover:bg-[#0E0E0E]/10"
                    >
                      View pup
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {!loading && tab === "upcoming" && (
        upcoming.length === 0 ? (
          <EmptyState
            testId="upcoming-empty"
            icon={<CalendarBlank size={32} weight="duotone" />}
            title="No upcoming litters"
            description="List a planned breeding to collect waitlist deposits and have a signable contract ready before puppies arrive."
            ctaLabel="Plan an upcoming litter"
            ctaTo="/litters/upcoming/new"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {upcoming.map((u) => (
              <div
                key={u.id}
                className="relative bg-white border border-[#EAEAEA] rounded-xl p-5 hover:border-ink transition"
                data-testid={`upcoming-card-${u.id}`}
              >
                <Link
                  to={`/litters/upcoming/${u.id}`}
                  className="absolute inset-0 rounded-xl"
                  aria-label={`Open upcoming litter ${u.dam_snapshot?.name || ""} × ${u.sire_snapshot?.name || ""}`}
                  data-testid={`upcoming-card-link-${u.id}`}
                />
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-crimson">
                      {u.stage || "draft"}
                    </p>
                    <h3 className="font-spectral text-xl tracking-tight mt-1">
                      {u.dam_snapshot?.name} × {u.sire_snapshot?.name}
                    </h3>
                    <p className="text-[12px] text-[#6B6B6B] mt-1">
                      {u.breed} · whelp {u.expected_whelp_date || "TBD"}
                    </p>
                  </div>
                  <div className="text-right flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-bold">
                        {DOLLARS(u.deposit_amount_cents)} deposit
                      </div>
                      <DeleteLitterButton
                        endpoint={`/upcoming-breedings/${u.id}`}
                        title={`Delete upcoming litter ${u.dam_snapshot?.name || ""} × ${u.sire_snapshot?.name || ""}?`}
                        description="This removes the listing from the marketplace and your litters. Deposit and payment records are archived for dispute handling. This can't be undone."
                        testId={`delete-upcoming-${u.id}`}
                        onDeleted={() => setUpcoming((prev) => prev.filter((x) => x.id !== u.id))}
                      />
                    </div>
                    {u.featured_until && new Date(u.featured_until) > new Date() && (
                      <span className="inline-block text-[9px] uppercase tracking-[0.2em] font-bold bg-gold text-ink px-1.5 py-0.5 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </AppShell>
  );
}
